import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { 
  HelpCircle, 
  MessageCircle, 
  Send, 
  CheckCircle,
  Clock,
  AlertCircle,
  Phone,
  X,
  Eye
} from 'lucide-react';

interface FAQ {
  question: string;
  answer: string;
}

interface SupportTicket {
  id: number;
  subject: string;
  status: string;
  created_at: string;
  updated_at: string;
  last_reply_at?: string | null;
  reply_count?: number;
  mobile_number?: string | null;
  message?: string;
}

interface TicketReply {
  id: number;
  ticket_id: number;
  sender: 'user' | 'admin';
  message: string;
  created_at: string;
}

interface TicketForm {
  subject: string;
  message: string;
  mobile_number?: string;
}

interface TicketDetail {
  ticket: SupportTicket;
  replies: TicketReply[];
}

const Support: React.FC = () => {
  const [faq, setFaq] = useState<FAQ[]>([]);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'faq' | 'contact' | 'tickets'>('faq');
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [_loadingTicket, setLoadingTicket] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [supportPhone, setSupportPhone] = useState<string>('');

  const { register, handleSubmit, reset, formState: { errors } } = useForm<TicketForm>();

  useEffect(() => {
    fetchFAQ();
    fetchTickets();
    fetchSupportPhone();
  }, []);

  const fetchSupportPhone = async () => {
    try {
      const response = await api.get('/config/pricing');
      if (response.data.success && response.data.support_phone) {
        setSupportPhone(response.data.support_phone);
      }
    } catch (error) {
      // If config doesn't have support_phone, we'll handle it gracefully
      console.error('Failed to fetch support phone:', error);
    }
  };

  const fetchFAQ = async () => {
    try {
      const response = await api.get('/support/faq');
      if (response.data.success) {
        setFaq(response.data.faq);
      }
    } catch (error) {
      console.error('Failed to fetch FAQ:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await api.get('/support/tickets');
      if (response.data.success) {
        setTickets(response.data.tickets);
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId: number) => {
    setLoadingTicket(true);
    try {
      const response = await api.get(`/support/ticket/${ticketId}`);
      if (response.data.success) {
        setSelectedTicket({
          ticket: response.data.ticket,
          replies: response.data.replies || []
        });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load ticket details');
    } finally {
      setLoadingTicket(false);
    }
  };

  const onSubmit = async (data: TicketForm) => {
    setSubmitting(true);
    try {
      const response = await api.post('/support/ticket', data);
      if (response.data.success) {
        toast.success('Support ticket created successfully!');
        reset();
        fetchTickets();
        setActiveTab('tickets');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to create support ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (ticketId: number) => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setReplying(true);
    try {
      const response = await api.post(`/support/ticket/${ticketId}/reply`, {
        message: replyMessage
      });
      if (response.data.success) {
        toast.success('Reply sent successfully');
        setReplyMessage('');
        // Refresh ticket details
        if (selectedTicket) {
          await fetchTicketDetails(ticketId);
        }
        // Refresh ticket list
        await fetchTickets();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'text-blue-600 bg-blue-100';
      case 'in_progress':
        return 'text-yellow-600 bg-yellow-100';
      case 'resolved':
        return 'text-green-600 bg-green-100';
      case 'closed':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="h-4 w-4" />;
      case 'in_progress':
        return <Clock className="h-4 w-4" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4" />;
      case 'closed':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading support...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Support Center</h1>
        <p className="text-secondary-600">
          Get help with your myoneqr account and profile
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('faq')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'faq'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            <HelpCircle className="h-4 w-4 inline mr-2" />
            FAQ
          </button>
          <button
            onClick={() => setActiveTab('contact')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'contact'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            <MessageCircle className="h-4 w-4 inline mr-2" />
            Contact Us
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tickets'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            <MessageCircle className="h-4 w-4 inline mr-2" />
            My Tickets
          </button>
        </nav>
      </div>

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-secondary-900 mb-6">Frequently Asked Questions</h2>
          {faq.map((item, index) => (
            <div key={index} className="card">
              <h3 className="text-lg font-medium text-secondary-900 mb-3">{item.question}</h3>
              <p className="text-secondary-600">{item.answer}</p>
            </div>
          ))}
        </div>
      )}

      {/* Contact Tab */}
      {activeTab === 'contact' && (
        <div className="card">
          <h2 className="text-2xl font-semibold text-secondary-900 mb-6">Contact Support</h2>
          <p className="text-secondary-600 mb-6">
            Can't find what you're looking for? Send us a message and we'll get back to you as soon as possible.
          </p>

          {/* Support Phone Display */}
          {supportPhone && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-blue-600" />
                <p className="text-sm text-blue-900">
                  <strong>Need urgent help?</strong> Call us at <strong>{supportPhone}</strong>
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-secondary-700 mb-2">
                Subject *
              </label>
              <input
                {...register('subject', { required: 'Subject is required' })}
                type="text"
                id="subject"
                className={`input-field ${errors.subject ? 'border-red-300' : ''}`}
                placeholder="Brief description of your issue"
              />
              {errors.subject && (
                <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-secondary-700 mb-2">
                Message *
              </label>
              <textarea
                {...register('message', { required: 'Message is required' })}
                id="message"
                rows={6}
                className={`input-field ${errors.message ? 'border-red-300' : ''}`}
                placeholder="Please provide as much detail as possible about your issue..."
              />
              {errors.message && (
                <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="mobile_number" className="block text-sm font-medium text-secondary-700 mb-2">
                Mobile Number (Optional)
              </label>
              <input
                {...register('mobile_number')}
                type="tel"
                id="mobile_number"
                className="input-field"
                placeholder="+91 XXXXX XXXXX"
              />
              <p className="mt-1 text-xs text-secondary-500">
                Provide your mobile number if you'd like us to contact you via phone
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary px-6 py-3 text-white hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tickets Tab */}
      {activeTab === 'tickets' && (
        <div>
          <h2 className="text-2xl font-semibold text-secondary-900 mb-6">My Support Tickets</h2>
          
          {tickets.length === 0 ? (
            <div className="card text-center py-12">
              <MessageCircle className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 mb-2">No tickets yet</h3>
              <p className="text-secondary-600 mb-4">
                You haven't submitted any support tickets yet.
              </p>
              <button
                onClick={() => setActiveTab('contact')}
                className="btn-primary text-white hover:text-white"
              >
                Create a Ticket
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {tickets.map((ticket) => (
                <div key={ticket.id} className="card hover:shadow-md transition-shadow cursor-pointer" onClick={() => fetchTicketDetails(ticket.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-medium text-secondary-900">
                          {ticket.subject}
                        </h3>
                        {ticket.reply_count !== undefined && ticket.reply_count > 0 && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                            {ticket.reply_count} {ticket.reply_count === 1 ? 'reply' : 'replies'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-secondary-600">
                        Created: {new Date(ticket.created_at).toLocaleDateString()}
                        {ticket.last_reply_at && (
                          <span className="ml-2">
                            • Last reply: {new Date(ticket.last_reply_at).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                        {getStatusIcon(ticket.status)}
                        <span className="ml-1 capitalize">{ticket.status.replace('_', ' ')}</span>
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchTicketDetails(ticket.id);
                        }}
                        className="p-2 text-secondary-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTicket(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-secondary-900">Ticket #{selectedTicket.ticket.id}</h2>
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Ticket Info */}
              <div className="border-b border-secondary-200 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-secondary-900">{selectedTicket.ticket.subject}</h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTicket.ticket.status)}`}>
                    {getStatusIcon(selectedTicket.ticket.status)}
                    <span className="ml-1 capitalize">{selectedTicket.ticket.status.replace('_', ' ')}</span>
                  </span>
                </div>
                <p className="text-sm text-secondary-600">
                  Created: {new Date(selectedTicket.ticket.created_at).toLocaleString()}
                </p>
                {selectedTicket.ticket.mobile_number && (
                  <p className="text-sm text-secondary-600 mt-1">
                    Mobile: {selectedTicket.ticket.mobile_number}
                  </p>
                )}
              </div>

              {/* Original Message */}
              <div className="bg-secondary-50 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-secondary-900">Your Message</span>
                      <span className="text-xs text-secondary-500">
                        {new Date(selectedTicket.ticket.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-secondary-700 whitespace-pre-wrap">{selectedTicket.ticket.message}</p>
                  </div>
                </div>
              </div>

              {/* Replies */}
              {selectedTicket.replies.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-secondary-900">Conversation</h4>
                  {selectedTicket.replies.map((reply) => (
                    <div
                      key={reply.id}
                      className={`p-4 rounded-lg ${
                        reply.sender === 'admin' ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-secondary-50 border-l-4 border-secondary-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-sm font-semibold ${
                          reply.sender === 'admin' ? 'text-blue-900' : 'text-secondary-900'
                        }`}>
                          {reply.sender === 'admin' ? 'Support Team' : 'You'}
                        </span>
                        <span className="text-xs text-secondary-500">
                          {new Date(reply.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className={`whitespace-pre-wrap ${
                        reply.sender === 'admin' ? 'text-blue-800' : 'text-secondary-700'
                      }`}>
                        {reply.message}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Box */}
              <div className="border-t border-secondary-200 pt-4">
                <h4 className="font-semibold text-secondary-900 mb-3">Add a Reply</h4>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={4}
                  className="input-field w-full mb-3"
                  placeholder="Type your reply here..."
                />
                <button
                  onClick={() => handleReply(selectedTicket.ticket.id)}
                  disabled={replying || !replyMessage.trim()}
                  className="btn-primary px-6 py-2 text-white hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {replying ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
